import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";

export function SignIn() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [google, setGoogle] = useState(false);
  // Which ways in the project actually offers. Asking beats assuming: a
  // provider that is in the code but not enabled on the project answers
  // "Unsupported provider" only after someone has already pressed the button.
  const [hasGoogle, setHasGoogle] = useState(false);

  useEffect(() => {
    const url = import.meta.env.VITE_SUPABASE_URL;
    fetch(`${url}/auth/v1/settings`, {
      headers: { apikey: import.meta.env.VITE_SUPABASE_ANON_KEY },
    })
      .then((response) => response.json())
      .then((settings) => setHasGoogle(Boolean(settings?.external?.google)))
      .catch(() => setHasGoogle(false));
  }, []);

  async function send(event: React.FormEvent) {
    event.preventDefault();
    // Sending takes a second, and nothing used to say so — pressing again sent
    // a second email, and enough presses hit the provider's rate limit, so the
    // reward for impatience was being locked out of your own account.
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: `${location.origin}/` },
      });
      if (error) setError(error.message); else setSent(true);
    } finally {
      setBusy(false);
    }
  }

  async function withGoogle() {
    if (google || busy) return;
    setGoogle(true);
    setError(null);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${location.origin}/` },
    });
    // On success the browser leaves for Google and never gets here, so this
    // only runs when the redirect could not be started.
    if (error) {
      setError(error.message);
      setGoogle(false);
    }
  }

  return (
    <div className="page narrow">
      <h1 className="serif hero-small">Sign in to keep your setlists</h1>
      <p className="muted">
        Songs and chords are open to everyone. An account is only for what is yours —
        setlists, fixes with your name on them, and the songbook on your phone.
      </p>

      {sent ? (
        <div className="card" style={{ marginTop: 24 }}>
          <strong>Check your email.</strong>
          <p className="muted" style={{ margin: "6px 0 0" }}>
            The link signs you in — there is no password to forget.
          </p>
        </div>
      ) : (
        <>
        {hasGoogle && (
        <button className="btn google-btn" onClick={withGoogle} disabled={google || busy}>
          {/* Google's mark, drawn rather than loaded: the page pulls no
              third-party assets, and the privacy note says so. */}
          <svg width="17" height="17" viewBox="0 0 18 18" aria-hidden="true">
            <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z"/>
            <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z"/>
            <path fill="#FBBC05" d="M3.97 10.72a5.4 5.4 0 0 1 0-3.44V4.95H.96a9 9 0 0 0 0 8.1l3.01-2.33Z"/>
            <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z"/>
          </svg>
          {google ? "Opening Google…" : "Continue with Google"}
        </button>
        )}

        {hasGoogle && <div className="or-rule"><span>or</span></div>}

        <form onSubmit={send} style={hasGoogle ? undefined : { marginTop: 24 }}>
          <label className="mono small muted" htmlFor="email">EMAIL</label>
          <input id="email" className="search" type="email" required value={email}
                 onChange={(event) => setEmail(event.target.value)}
                 disabled={busy}
                 placeholder="you@example.com" />
          <button className="btn btn-primary" style={{ width: "100%", marginTop: 10 }}
                  disabled={busy}>
            {busy ? "Sending…" : "Send me a link"}
          </button>
          {error && <p className="small" style={{ color: "#a33" }}>{error}</p>}
        </form>
        </>
      )}
    </div>
  );
}
